import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

type Issuance = {
  id: string;
  recipientName: string;
  recipientCompany: string | null;
  createdAt: string;
  firstOpenedAt: string | null;
  completedAt: string | null;
};

type DashboardSummary = {
  playlistsCreated: number;
  recipientsSentTo: number;
  uniqueOpens: number;
  sparkline: Array<{ day: string; count: number }>;
};

export function DashboardPage() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["dashboard-issuances"],
    queryFn: () => api.get<{ items: Issuance[] }>("/api/admin/issuances"),
  });
  // Audit V1: distinct counters from the dashboard endpoint.
  const { data: summary } = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: () => api.get<DashboardSummary>("/api/admin/analytics/dashboard"),
  });
  const recent = (data?.items ?? []).slice(0, 6);
  const totals = {
    sent: data?.items.length ?? 0,
    opened: data?.items.filter((i) => i.firstOpenedAt).length ?? 0,
    completed: data?.items.filter((i) => i.completedAt).length ?? 0,
  };
  // Sparkline: pad missing days with 0 so the trend reflects calendar gaps.
  const sparkBars = paddedSparkline(summary?.sparkline ?? []);

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-2xl font-semibold">
            Welcome back, {user?.name}
          </h1>
          <p className="text-sm text-muted-foreground">
            Build, send, and watch engagement on personalized video packs.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/playlists/new">
            <Button variant="outline">New playlist</Button>
          </Link>
          <Link to="/admin/issuances/new">
            <Button>Compose pack</Button>
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader><CardTitle>Packs sent</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-semibold text-gold">{totals.sent}</div></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Opened</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-semibold text-gold">{totals.opened}</div></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Completed</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-semibold text-gold">{totals.completed}</div></CardContent>
        </Card>
      </div>
      {/* Audit V1: distinct counters + sparkline */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader><CardTitle>Playlists created</CardTitle></CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{summary.playlistsCreated}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Recipients sent to</CardTitle></CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{summary.recipientsSentTo}</div>
              <div className="text-xs text-muted-foreground mt-1">distinct emails</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Unique opens</CardTitle></CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{summary.uniqueOpens}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary.recipientsSentTo > 0
                  ? `${Math.round((summary.uniqueOpens / summary.recipientsSentTo) * 100)}% open rate`
                  : "—"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Last 30 days</CardTitle></CardHeader>
            <CardContent>
              <Sparkline bars={sparkBars} />
            </CardContent>
          </Card>
        </div>
      )}
      <Card>
        <CardHeader><CardTitle>Recent packs</CardTitle></CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">No packs sent yet.</p>
          ) : (
            <ul className="divide-y divide-admin-rule">
              {recent.map((i) => (
                <li key={i.id}>
                  <Link to={`/admin/issuances/${i.id}`} className="flex items-center justify-between py-3 hover:bg-foreground/5 -mx-2 px-2 rounded-md">
                    <div>
                      <div className="font-medium">{i.recipientName}</div>
                      <div className="text-xs text-muted-foreground">{i.recipientCompany ?? "—"} · {new Date(i.createdAt).toLocaleDateString()}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {i.completedAt ? "Completed" : i.firstOpenedAt ? "Opened" : "Unopened"}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function paddedSparkline(rows: Array<{ day: string; count: number }>): number[] {
  const map = new Map(rows.map((r) => [r.day, r.count]));
  const out: number[] = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86_400_000);
    const key = d.toISOString().slice(0, 10);
    out.push(map.get(key) ?? 0);
  }
  return out;
}

function Sparkline({ bars }: { bars: number[] }) {
  const max = Math.max(1, ...bars);
  return (
    <div className="flex items-end gap-[1px] h-12">
      {bars.map((v, i) => (
        <div
          key={i}
          className="flex-1 bg-gold/70 hover:bg-gold rounded-sm"
          style={{ height: `${Math.max(2, (v / max) * 100)}%` }}
          title={`${v}`}
        />
      ))}
    </div>
  );
}
